/**
 * Walkthrough #122 (2026-09-30): the N+1 prefetch runs while the candidate is
 * still on N, so it asks the server NOT to start N+1's per-question clock
 * (`?prefetch=1`). The question then renders from cache on arrival with no
 * GET of its own — so the arrival has to send one plain GET to start the
 * clock. This remembers which positions were warmed without stamping.
 */
const warmed = new Set<string>();

const keyOf = (attemptId: string, position: number) => `${attemptId}:${position}`;

export function markPrefetched(attemptId: string, position: number): void {
  warmed.add(keyOf(attemptId, position));
}

/** True once per warmed position — the caller then sends the stamping GET. */
export function takePrefetched(attemptId: string, position: number): boolean {
  return warmed.delete(keyOf(attemptId, position));
}

/**
 * The picture route serves only a position whose clock has started
 * (`answer.started_at`). On a warmed position the `<img>` therefore has to
 * wait for the stamping GET: sent together they race, the picture loses (404)
 * and the candidate reads "The picture could not be loaded" (found by the
 * 2026-09-30 verify — before #122 the warm-up itself had started the clock).
 * True from the first render on a warmed position until that GET has settled.
 */
export function awaitsClock(
  attemptId: string | null,
  position: number,
  stampingPosition: number | null,
): boolean {
  if (stampingPosition === position) return true;
  return attemptId !== null && warmed.has(keyOf(attemptId, position));
}

/**
 * Walkthrough #112 (2026-09-30): on a forward-only test a submitted position
 * can still be reopened by URL, but the server refuses to overwrite it and the
 * save queue swallows that 409 — the candidate's change vanished silently. Such
 * a position is shown locked and never re-saved.
 */
export function isLockedSubmitted(q: {
  allow_back_navigation: boolean;
  submitted?: boolean | null;
}): boolean {
  return !q.allow_back_navigation && q.submitted === true;
}

/**
 * FEAT-18: whether the editor's "Reset to the starting code" must also
 * overwrite the code the server holds for this position.
 *
 * Starter code counts as "not answered", and moving on from an unanswered
 * question saves nothing — so a reset that only changed the editor would
 * leave the server with the code the candidate just threw away: graded at
 * submit, and shown again on Back. Hence: overwrite whenever there IS a saved
 * copy.
 *
 * And ONLY then. A save finalizes the position, and on a forward-only test a
 * finalized position refuses every later write — saving on a first visit
 * would lock the question the candidate is still working on (there, nothing
 * was saved yet, so there is nothing to overwrite). A locked position is
 * never written at all (#112).
 */
export function resetOverwritesSavedCode(q: {
  allow_back_navigation: boolean;
  submitted?: boolean | null;
  saved_final_code?: string | null;
}): boolean {
  if (q.saved_final_code === null || q.saved_final_code === undefined) return false;
  return !isLockedSubmitted(q);
}

/**
 * FEAT-18 verify (2026-10-03): the reset's own save is PROVISIONAL on a
 * forward-only test — stored, never finalized.
 *
 * There, a saved copy that is not yet final exists only after the page's own
 * pre-deadline save (#39/#84). An ordinary save finalized the position in the
 * candidate's last seconds, so their "Submit & next" of the code they typed
 * after the reset was refused (409) and the starter was graded. The server
 * honours `provisional` only near the deadline — the one place this save can
 * happen on such a test. Under free navigation a final save locks nothing.
 */
export function resetSaveIsProvisional(q: { allow_back_navigation: boolean }): boolean {
  return !q.allow_back_navigation;
}
